/**
 * Calls to the non-custodial endpoints of both APIs.
 *
 * The pattern is always the same: `prepare` returns a hash, the browser signs it
 * locally, `execute` submits the signature. A prepared transaction expires on the Canton side
 * in ~30 seconds — so a `prepare` must never be pre-fetched or cached,
 * it is triggered by the user's click and followed immediately by the signature.
 */

import { authFetch, AUTH_API_URL } from '../config/api';

const BRIDGE_API = 'https://nuxarisapiv1.xyz/api/bridge';

export interface PrepareResult {
  requestId: string;
  hashB64: string;
  expiresAt: string;
  /** 'offer' = the recipient has to accept; the CC are held until they do. */
  mode?: 'direct' | 'offer';
  offerExpiresAt?: string | null;
  intent?: Record<string, unknown>;
}

export interface PreapprovalStatus {
  active: boolean;
  provider?: string;
  isOurProvider?: boolean;
  expiresAt?: string;
  expiringSoon?: boolean;
  unknown?: boolean;
}

/**
 * A failure the caller may retry, because the network was busy rather than the request wrong.
 *
 * The distinction has to survive the trip through `call`: a plain Error would flatten it into
 * a string, and the retry loop would either give up on congestion or hammer a genuine failure
 * forever. The server states it explicitly (503 + congested:true) rather than us guessing from
 * message text, so the browser and the worker agree on what "busy" means.
 */
export class CongestedError extends Error {
  readonly congested = true;
  constructor(message: string) {
    super(message);
    this.name = 'CongestedError';
    // Required for `instanceof` to work once TypeScript downlevels to ES5, which CRA does.
    Object.setPrototypeOf(this, CongestedError.prototype);
  }
}

/**
 * ⚠️ The refusal's FIELDS are carried onto the Error, not just its message.
 *
 * The server distinguishes a refusal the member can fix — their own wallet cannot receive an
 * asset directly — from one they cannot, and says which party is at fault. Dropping `code`,
 * `blockedParty` and `asset` here left the page able to show the sentence but not to aim the
 * reception banner it already has, so the one refusal with a button behind it looked like every
 * other failure. `message` is untouched, so every existing `err.message` reader is unaffected.
 */
async function call<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await authFetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const body = data as {
      error?: string; congested?: boolean; code?: string;
      blockedParty?: string; asset?: string; retryAfterSec?: number;
    };
    const message = body.error || `Request failed (${res.status})`;
    const err = body.congested ? new CongestedError(message) : new Error(message);
    if (body.code) (err as any).code = body.code;
    // ⚠️ `retryAfterSec` aussi : un refus de limiteur sans son delai se presente comme une panne
    // plutot que comme une pause. Aucun chemin de ce client ne le leve aujourd'hui, mais l'omettre
    // serait un manque silencieux le jour ou un limiteur passe par ici.
    if (body.retryAfterSec !== undefined) (err as any).retryAfterSec = body.retryAfterSec;
    if (body.blockedParty) (err as any).blockedParty = body.blockedParty;
    if (body.asset) (err as any).asset = body.asset;
    throw err;
  }
  return data as T;
}

const post = (body?: unknown): RequestInit =>
  ({ method: 'POST', body: body ? JSON.stringify(body) : undefined });

/* ------------------------------------------------------------------ etat du wallet */

export async function fetchWalletStatus(): Promise<{ partyId: string; preapproval: PreapprovalStatus }> {
  return call(`${BRIDGE_API}/wallet/status`);
}

export interface AccountStatus {
  banned: boolean;
  bannedAt?: string;
  reason?: string | null;
}

/**
 * Whether this account may use the bridge.
 *
 * Separate from `fetchWalletStatus`, which 403s when there is no provisioned party — a banned
 * user has to be told why the bridge is closed to them whatever state their wallet is in.
 */
export async function fetchAccountStatus(): Promise<AccountStatus> {
  return call(`${BRIDGE_API}/account/status`);
}

/* --------------------------------------------- deposit for a Canton -> EVM order */

export function prepareOrderSignature(orderId: string): Promise<PrepareResult> {
  return call(`${BRIDGE_API}/order/canton/${orderId}/prepare`, post());
}

/**
 * ⚠️ `status` is `'completed'` on the atomic Canton pair and `'signed'` everywhere else, and
 * `amountOut` only comes with the former. On the atomic path both transfers happen inside the
 * transaction the member just signed, so there is nothing left to wait for — the order is already
 * settled when this resolves.
 */
export function executeOrderSignature(orderId: string, requestId: string, signature: string) {
  return call<{ status: string; orderId: string; updateId: string; amountOut?: number }>(
    `${BRIDGE_API}/order/canton/${orderId}/execute`,
    post({ requestId, signature }),
  );
}

/* ---------------------------------------------------------- free-form CC send */

export function prepareWalletSend(receiverPartyId: string, amount: number, memo?: string, asset?: string): Promise<PrepareResult> {
  return call(`${BRIDGE_API}/wallet/send/prepare`, post({ receiverPartyId, amount, memo, asset }));
}

/**
 * Ce qu'un envoi vers cette adresse va devenir, demande AVANT de preparer quoi que ce soit.
 *
 * `prepare -> signature -> execute` s'enchaine sans pause (le hash expire en ~30 s), donc il n'y
 * a pas de place pour une confirmation entre les deux. C'est ici, pendant que le membre peut
 * encore renoncer, qu'on peut lui dire que ses CC seront retenus jusqu'a acceptation.
 *
 * `unknown` quand on n'a pas pu demander — la page traite ce cas comme une offre, jamais comme
 * un transfert direct : rassurer a tort ne surprend personne, l'inverse fait ouvrir un ticket.
 */
export function checkRecipient(partyId: string) {
  return call<{ partyId: string; mode: 'direct' | 'offer' | 'unknown' }>(
    `${BRIDGE_API}/wallet/recipient?partyId=${encodeURIComponent(partyId)}`,
  );
}

/**
 * Retirer une offre qu'on a emise et que personne n'a acceptee — les CC reviennent tout de suite.
 *
 * L'expiration les rend de toute facon au bout de 24 h ; ceci evite d'attendre un jour pour
 * recuperer son propre argent quand on s'est trompe d'adresse.
 */
export function prepareWithdrawOffer(contractId: string) {
  return call<PrepareResult>(`${BRIDGE_API}/wallet/outgoing/withdraw/prepare`, post({ contractId }));
}

export function executeWithdrawOffer(requestId: string, signature: string) {
  return call<{ status: string; amount: number | null; updateId: string }>(
    `${BRIDGE_API}/wallet/outgoing/withdraw/execute`, post({ requestId, signature }),
  );
}

export function executeWalletSend(requestId: string, signature: string) {
  return call<{ status: string; amount: number; updateId: string; offerExpiresAt?: string | null }>(
    `${BRIDGE_API}/wallet/send/execute`,
    post({ requestId, signature }),
  );
}

/* -------------------------------------------------------------- Nuxaris Premium */

export interface PremiumStatus {
  premium: boolean;
  until: string | null;
  daysLeft: number | null;
  /** Maintenance pause: the countdown is frozen, so `daysLeft` is the figure to show. */
  paused?: boolean;
  /** Price of one period, in CC. Always read from the server — never hardcode it here. */
  priceCc: number;
  periodDays: number;
}

/** Result of a premium payment. `received` means paid on-chain but not yet credited. */
export interface PremiumPaymentResult extends Partial<PremiumStatus> {
  status: 'active' | 'received';
  paymentId: string;
  updateId: string;
  message?: string;
}

export function fetchPremiumStatus(): Promise<PremiumStatus> {
  return call(`${BRIDGE_API}/premium/status`);
}

/**
 * The Premium terms the member is shown and must tick before paying.
 *
 * The exact string travels to the server and is stored on the payment: what has to be provable
 * later is not that a box was ticked but WHICH wording it was ticked against.
 */
export const PREMIUM_TERMS_VERSION = '2026-08-20';

/** Mints the payment reference and returns the hash to sign. Same 30s rule as any prepare. */
export function preparePremiumPayment(): Promise<PrepareResult & {
  intent: { paymentId: string; amount: number; periodDays: number };
}> {
  return call(`${BRIDGE_API}/premium/subscribe/prepare`, post({ acceptedTerms: PREMIUM_TERMS_VERSION }));
}

export function executePremiumPayment(requestId: string, signature: string): Promise<PremiumPaymentResult> {
  return call(`${BRIDGE_API}/premium/subscribe/execute`, post({ requestId, signature }));
}

/* ------------------------------------------------------- Daily quests & points */

export interface QuestProgress {
  current?: number;
  target?: number;
  /**
   * ⚠️ Le serveur envoyait deja 'tx' (quete de rechargement) sans que ce type le dise : l'union
   * ne decrivait pas ce qui arrive vraiment, et seul un litteral cote front s'y heurtait. Les
   * quatre valeurs sont celles que `getBoard` peut rendre.
   */
  unit?: 'usd' | 'order' | 'tx' | 'referral';
  orders?: { current: number; target: number };
  usd?: { current: number; target: number };
  activeDays?: number;
  minDays?: number;
  /** Repeatable quests only: what today has actually earned, since there is no target. */
  bridges?: number;
  points?: number;
}

export interface Quest {
  id: string;
  title: string;
  description: string;
  points: number;
  cadence: 'daily' | 'weekly';
  completed: boolean;
  /**
   * No ceiling and no completion: it scores once per bridge, for ever.
   *
   * `completed` is meaningless on these and the server pins it to false, so the page must
   * branch on this rather than read a flag that can only ever say "not done".
   */
  repeatable?: boolean;
  progress: QuestProgress;
  /** Weekly quests only: what still counted today once the per-day ceiling applied. */
  dailyCap?: {
    orders: number;
    usd: number;
    countedToday: { orders: number; usd: number };
    reachedToday: boolean;
  };
  /**
   * The referral quest only: the member's own invite code for this week.
   *
   * `null` rather than absent when there is none — a free member has no code, and the card has
   * to tell that apart from a code that has not arrived yet.
   */
  referral?: {
    code: string;
    /** True once somebody has signed up with it. The points still wait for their membership. */
    used: boolean;
    expiresAt: string;
  } | null;
}

export interface QuestBoard {
  /** Master switch on the server. False = the system is not live yet. */
  enabled: boolean;
  premium: boolean;
  questDay: string;
  dayEndsAt: string;
  quests: Quest[];
  /** Minimum spacing between two orders for both to count towards quests. */
  orderCooldownSeconds?: number;
  /**
   * The best a week can score, from the server's own catalogue.
   *
   * Read rather than assumed: it was 31 while Solana was the only provided route and became 52
   * when Base joined. A number written into this page is a number that quietly disagrees with
   * the backend the next time a quest is added.
   */
  maxWeeklyPoints?: number;
  /**
   * A live point freeze, or null.
   *
   * ⚠️ Without this the page shows bars that move and a total that never does, which reads as a
   * broken page and arrives as a support ticket. It has to be able to say "points are paused until
   * <date>" — that is the difference between a sanction and a bug.
   */
  restriction?: { endsAt: string; notice: string } | null;
  /** When the next bridge will count. Null means it counts right now. */
  nextCountsAt?: string | null;
  /** The last bridge that actually counted — the one the window is measured from. */
  lastCountedAt?: string | null;
  weekPoints: number;
  lifetimePoints: number;
  weekStartsAt: string;
  weekResetsAt: string;
  /** What the member has actually been paid, from the payout ledger — not what points are worth. */
  lifetimeCc?: number;
  last30Cc?: number;
}

export interface RewardsPoints {
  weekPoints: number;
  lifetimePoints: number;
  weekStartsAt: string;
  weekResetsAt: string;
  lifetimeCc: number;
  last30Cc: number;
}

/** Le crédit de trafic d'un membre, et ce qu'une transaction coûte à l'instant. */
export interface TrafficStatus {
  octets: number;
  reserved: number;
  available: number;
  /** Ce que le membre voit : des transactions, pas des octets. */
  tx: number;
  txHeld: number;
  octetsPerTx: number;
  /** Null quand l'exchange est injoignable — on affiche le solde et on dit que l'achat est indisponible. */
  priceCcPerTx: number | null;
  priceUsdPerTx: number | null;
  /** Ce que coute UN swap same-ledger, en ecritures, et combien le solde en couvre.
   *  ⚠️ Envoyes par le serveur, jamais calcules ici : le prix d'une route est derive de
   *  `cantonWritesFor`, et une constante dans le front est une seconde definition — celle qui
   *  derive est toujours celle que le membre lit. */
  swapWrites?: number;
  swapsLeft?: number;
  /**
   * Le credit restant VALORISE en CC, pas un solde en CC.
   *
   * ⚠️ Ce qui a ete achete, ce sont des octets, et ils ne bougent pas. Si le CC double, les
   * memes octets « valent » deux fois moins de CC tout en payant exactement autant de bridges.
   * D'ou le « ≈ » partout ou ce nombre s'affiche : presente comme un solde, il ressemble a de
   * l'argent qui disparait — c'est la forme des deux tickets deja traites sur la decote.
   */
  valueCc: number | null;
  valueUsd: number | null;
  /** False tant que le blocage n'est pas allumé : la section reste lisible avant de mordre. */
  required: boolean;
}

export interface TrafficQuote {
  amountCc: number;
  transactions: number;
  transactionsGross: number;
  purchaseFeeTx: number;
  ccPerTx: number;
  usdPerTx: number;
}

export interface TrafficEntry {
  // ⚠️ Every kind the server can send. The panel used to know four, so a `free` ride rendered with
  // a blank title and a minus sign — see utils/trafficHistory.ts.
  kind: 'buy' | 'reserve' | 'spend' | 'release' | 'free' | 'welcome' | 'adjustment' | 'refund';
  octets: number;
  tx: number;
  orderId: string | null;
  note: string | null;
  at: string;
}

export function fetchTrafficStatus(): Promise<TrafficStatus> {
  return call(`${BRIDGE_API}/traffic/status`);
}

export function fetchTrafficQuote(amountCc: number): Promise<TrafficQuote> {
  return call(`${BRIDGE_API}/traffic/quote?amount=${encodeURIComponent(amountCc)}`);
}

/** The validator's free allowance, as the server last saw it. */
export interface FreeTraffic {
  ok: boolean;
  /** Usable now: the pot minus what is already promised to bridges in progress. */
  freeOctets: number | null;
  /** The pot itself, before that subtraction. */
  poolOctets?: number | null;
  /** Octets committed to orders still in flight — the gauge cannot see them yet. */
  heldOctets?: number;
  capOctets: number | null;
  perTxOctets: number;
  transactionsFree: number | null;
  /**
   * Transactions held back and never given away. Anything that displays a free-bridge count
   * MUST subtract it: the gate refuses below perTxOctets x (1 + reserveTx), so a count that
   * ignores it offers bridges the server will not grant.
   */
  reserveTx: number;
  transactionsCap: number | null;
  /** Octets per second, or null until the gauge has actually been seen climbing. */
  ratePerSec: number | null;
  at: number | null;
  ageMs: number | null;
  eligible: boolean;
  /**
   * The latest free bridge granted to someone else in the last hour, or null. The ONLY thing
   * allowed to make the banner say "someone just took one". `ageMs` is measured by the server's
   * clock, so a client clock that is off cannot turn an old grant into a new one.
   */
  lastGrant?: { id: string; ageMs: number } | null;
  /**
   * The member's OWN hourly limit (09/10): at most `cap` free bridges in any 60 minutes, then they
   * pay from their credit even while the pot has room. `left` is what they can still take, and
   * `nextAt` (ISO) is when the next one opens once they are all used. Null when there is no limit
   * or the member is not eligible.
   */
  hourly?: { cap: number; used: number; left: number | null; nextAt: string | null } | null;
  enabled: boolean;
  required: boolean;
}

export function fetchFreeTraffic(): Promise<FreeTraffic> {
  return call(`${BRIDGE_API}/traffic/free`);
}

export function fetchTrafficHistory(): Promise<{ entries: TrafficEntry[] }> {
  return call(`${BRIDGE_API}/traffic/history`);
}

/** One settled top-up: the CC it cost and the bridges it bought. */
export interface TrafficPurchase {
  id: string;
  amountCc: number;
  /** Bridges credited, fractional (the server divides octets by the per-bridge size). */
  bridges: number;
  at: string;
}

export function fetchTrafficPurchases(): Promise<{ purchases: TrafficPurchase[] }> {
  return call(`${BRIDGE_API}/traffic/purchases`);
}

export function prepareTrafficPurchase(amountCc: number) {
  return call(`${BRIDGE_API}/traffic/purchase/prepare`, {
    method: 'POST', body: JSON.stringify({ amountCc }),
  });
}

export function executeTrafficPurchase(requestId: string, signature: string) {
  return call(`${BRIDGE_API}/traffic/purchase/execute`, {
    method: 'POST', body: JSON.stringify({ requestId, signature }),
  });
}

export function fetchQuestBoard(): Promise<QuestBoard> {
  return call(`${BRIDGE_API}/rewards/quests`);
}

export function fetchRewardsPoints(): Promise<RewardsPoints> {
  return call(`${BRIDGE_API}/rewards/points`);
}

/* ------------------------------------------------------------ key-based sign-in */

export type AccountStep = 'password' | 'key' | 'verify' | 'unknown';

/**
 * Which credential the sign-in form should ask for. Only the server knows whether an
 * account still needs its email confirmed, already holds a wallet, or does not exist.
 *
 * The browser overrides a `key` answer when it holds a vault for that email: the password
 * then unlocks it locally and nothing more is asked of the server.
 */
export async function fetchAccountState(email: string): Promise<AccountStep> {
  const res = await fetch(`${AUTH_API_URL}/api/auth/account-state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    throw new Error(res.status === 429
      ? 'Too many attempts. Wait a minute and try again.'
      : 'Could not reach the server. Try again.');
  }
  const data = await res.json();
  return data.step as AccountStep;
}

export async function requestLoginChallenge(email: string): Promise<{ nonce: string; expiresAt: string }> {
  const res = await fetch(`${AUTH_API_URL}/api/auth/challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not start login');
  return data;
}

export async function loginWithKey(email: string, nonce: string, signature: string) {
  const res = await fetch(`${AUTH_API_URL}/api/auth/login-key`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, nonce, signature }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Invalid credentials');
  return data;
}

/* ------------------------------------------------ provisioning non-custodial */

export interface ProvisioningState {
  step: 'init' | 'preapproval' | 'done';
  partyId: string | null;
  cantonStatus: string | null;
  custodialKeyPresent: boolean;
}

export function fetchProvisioningState(): Promise<ProvisioningState> {
  return call(`${AUTH_API_URL}/api/auth/wallet/provisioning`);
}

export function initWallet(publicKeyHex: string): Promise<{ partyId: string; toSign: { index: number; hash: string }[] }> {
  return call(`${AUTH_API_URL}/api/auth/wallet/init`, post({ publicKeyHex }));
}

export function submitTopology(signatures: { index: number; signature: string }[]) {
  return call<{ partyId: string; nextStep: string; waitMs: number }>(
    `${AUTH_API_URL}/api/auth/wallet/topology-submit`,
    post({ signatures }),
  );
}

/**
 * `source` is only ever logged. It exists because "did the wizard create this, or did the
 * member tick the box?" could not be answered from the outside: both paths hit the same
 * endpoint, and the timing alone was ambiguous.
 */
export function preparePreapproval(source: 'wizard' | 'wallet' | 'resume' = 'wallet'): Promise<{ hashB64: string }> {
  return call(`${AUTH_API_URL}/api/auth/wallet/preapproval/prepare`, post({ source }));
}

export function executePreapproval(signature: string) {
  return call<{ status: string; partyId: string }>(
    `${AUTH_API_URL}/api/auth/wallet/preapproval/execute`,
    post({ signature }),
  );
}

export { BRIDGE_API };

export interface RewardsPot {
  premiumMembers: number;
  /** What the week has earned so far. */
  accruedCc: number;
  /** Where it lands on Friday at the current rate. Null while the week is too young to say. */
  projectedCc: number | null;
  weekEndsAt: string;
  /** Points earned across all eligible members so far this week. */
  weekPoints: number;
  /** What that total is heading for by Friday — the denominator of anyone's share. */
  projectedWeekPoints: number;
  appRewardsCc: number;
  trafficCc: number;
  netCc: number;
  sharePercent: number;
  computedAt: string;
  stale?: boolean;
}

/**
 * The pot members are competing for, and how many of them there are.
 *
 * Cached server-side: computing it walks the validator's wallet history, so this is not a
 * figure to poll.
 */
export async function fetchRewardsPot(): Promise<RewardsPot> {
  return call(`${BRIDGE_API}/rewards/pot`);
}

/* ------------------------------------------------------- saved receiving addresses */

export interface SavedAddress {
  id: string;
  label: string;
  chain: string;
  address: string;
  createdAt: string;
  lastUsedAt: string | null;
}

/**
 * A member's own address book, narrowed to one chain.
 *
 * Always ask for the chain you are about to send on. The server scopes the answer, which is
 * what makes it impossible to pick an Ethereum address for a Solana payout: the wrong-chain
 * entries are never offered in the first place.
 */
export async function fetchSavedAddresses(chain: string): Promise<SavedAddress[]> {
  const data = await call<{ addresses: SavedAddress[] }>(
    `${BRIDGE_API}/wallet/addresses?chain=${encodeURIComponent(chain)}`,
  );
  return data.addresses || [];
}

export async function saveAddress(label: string, chain: string, address: string): Promise<SavedAddress> {
  const data = await call<{ address: SavedAddress }>(
    `${BRIDGE_API}/wallet/addresses`,
    post({ label, chain, address }),
  );
  return data.address;
}

export async function renameSavedAddress(id: string, label: string): Promise<void> {
  await call(`${BRIDGE_API}/wallet/addresses/${id}`, { method: 'PATCH', body: JSON.stringify({ label }) });
}

export async function deleteSavedAddress(id: string): Promise<void> {
  await call(`${BRIDGE_API}/wallet/addresses/${id}`, { method: 'DELETE' });
}

/* ------------------------------------------- transfers waiting to be accepted */

export interface IncomingTransfer {
  contractId: string;
  amount: number;
  sender: string | null;
  /** Present on the offers endpoint; the older /wallet/incoming does not send it. */
  receiver?: string | null;
  requestedAt: string | null;
  /** After this instant the CC unlock back to the sender and can no longer be accepted. */
  executeBefore: string | null;
  expired: boolean;
  memo: string | null;
  /** Which asset this offer is denominated in. Null when the registry is unknown to us. */
  symbol?: string | null;
}

/**
 * CC sent to this wallet that are locked awaiting acceptance.
 *
 * A sender who does not use this wallet's TransferPreapproval creates an instruction rather
 * than a deposit: the amount sits locked on their side until the receiver accepts it, or until
 * it expires back to them. Nothing showed these, so the CC were invisible to the only person
 * who could unlock them.
 */
export interface TransferOffers {
  incoming: IncomingTransfer[];
  /** Instructions this wallet SENT that nobody has accepted — the CC are locked until then. */
  outgoing: IncomingTransfer[];
  /** Null when the node could not be asked; the lists still stand. */
  receptionActive: boolean | null;
}

/**
 * Both directions in one call.
 *
 * The outgoing side is the half nobody could see: CC sent to a party that never accepted are
 * locked on the sender's side until the deadline, so the balance drops and the money is neither
 * spent nor received.
 */
export async function fetchTransferOffers(): Promise<TransferOffers> {
  return call<TransferOffers>(`${BRIDGE_API}/wallet/offers`);
}

export async function fetchIncomingTransfers(): Promise<IncomingTransfer[]> {
  const data = await call<{ transfers: IncomingTransfer[] }>(`${BRIDGE_API}/wallet/incoming`);
  return data.transfers || [];
}

/**
 * Reception approval for an asset other than Canton Coin.
 *
 * `active: null` means we could not read it — never treat it as off. Telling a member reception
 * is disabled when we simply failed to look sends them to sign a second preapproval for
 * something they already have.
 */
export interface ReceptionStatus { asset: string; active: boolean | null; configured: boolean; }

export async function fetchReception(asset: string): Promise<ReceptionStatus> {
  return call(`${BRIDGE_API}/wallet/reception?asset=${encodeURIComponent(asset)}`, {});
}

export async function prepareReception(asset: string): Promise<PrepareResult> {
  return call(`${BRIDGE_API}/wallet/reception/prepare`, post({ asset }));
}

export async function executeReception(requestId: string, signature: string): Promise<{ status: string; asset: string | null }> {
  return call(`${BRIDGE_API}/wallet/reception/execute`, post({ requestId, signature }));
}

export async function prepareAcceptTransfer(contractId: string): Promise<PrepareResult> {
  return call(`${BRIDGE_API}/wallet/incoming/accept/prepare`, post({ contractId }));
}

export async function executeAcceptTransfer(requestId: string, signature: string): Promise<{ status: string; amount: number | null }> {
  return call(`${BRIDGE_API}/wallet/incoming/accept/execute`, post({ requestId, signature }));
}


/* ------------------------------------------------------------------ leaderboard */

export interface LeaderRow {
  /** `null` = pas encore classe sur cette periode (aucun point marque). */
  rank: number | null;
  username: string;
  points: number;
  /** Ce membre a rattache un compte X. ⚠️ PAS « X l'a certifie » — glyphe X, jamais coche bleue. */
  xLinked?: boolean;
  xUsername?: string | null;
  /** Identifiant X, seulement s'il y a une photo stockee chez nous. */
  xAvatarId?: string | null;
  /** Sur `me` uniquement : ce qu'il manque pour doubler le rang au-dessus (jamais l'ecart nu). */
  toNext?: number | null;
  nextRank?: number | null;
}

export interface Leaderboard {
  scope: 'daily' | 'weekly' | 'lifetime';
  board: LeaderRow[];
  /** Le membre lui-meme, meme s'il est hors du top — souvent la seule ligne qui l'interesse. */
  me: LeaderRow | null;
  total: number;
  resetsAt: string | null;
  dayResetsAt: string | null;
}

/** L'URL de la photo, servie par notre API pour que X ne voie pas qui lit le classement. */
export function avatarUrl(xAvatarId: string): string {
  return `${BRIDGE_API}/avatar/${encodeURIComponent(xAvatarId)}`;
}

/* ────────────────────────────────────────────────────────────────────────────
   Les BOUGIES CC/cBTC de la page Trade.

   ⚠️ CE N'EST PAS UN MARCHE, C'EST NOTRE CROISEMENT : le cBTC est cote sur `BTC-USDT` et le CC
   sur `CC-USDT`, donc ce taux vaut `prix(CC) / prix(BTC)` — exactement ce que calcule le devis.
   La courbe est la suite des prix qu'on AURAIT cotes, et elle ne peut pas contredire le ticket
   d'ordre juste a cote. Une autre source (un DEX Canton) serait un prix qu'on ne sert pas.

   ⚠️ LES BOUGIES SONT AGREGEES DEPUIS CES CROISEMENTS, jamais depuis l'OHLC des deux jambes :
   diviser champ par champ surestime le range d'un facteur median 3,9 et inverse la bougie sur
   3,3 % des heures. Detail complet en tete de `services/cross-series.js`.
   ──────────────────────────────────────────────────────────────────────────── */
export type CrossPeriod = '24h' | '7d' | '1m' | 'all';

/**
 * `[time, open, high, low, close]` — `time` en **SECONDES** UTC, le format de lightweight-charts.
 * ⚠️ Ne PAS multiplier par 1000 : les bougies atterriraient en l'an 58 000.
 *
 * Un seau sans aucune observation arrive comme **`[time]` seul** : c'est un TROU, que le graphique
 * dessine comme tel (`WhitespaceData`). Le combler ou l'omettre collerait les bougies voisines et
 * une panne de six heures se lirait comme six heures qui n'ont pas existe — le defaut de timeline
 * corrige le 07/07.
 */
export type CrossCandle = [number] | [number, number, number, number, number];

export interface CrossSeries {
  period: CrossPeriod;
  candles: CrossCandle[];
  /** Vrai avant le premier rafraichissement : c'est un chargement, pas une panne. */
  warming?: boolean;
  /** Vrai quand la derniere lecture a echoue et qu'on sert la precedente. */
  stale?: boolean;
  granularity?: string;
  first?: number;
  last?: number;
  min?: number;
  max?: number;
  /** Croisements observes — la profondeur reelle de la serie, pas le nombre de bougies. */
  samples?: number;
  /** Bougies reposant sur moins de 3 observations : vraies, mais peu informatives. */
  thinCandles?: number;
  /** Le dernier seau est encore en formation, donc sa cloture bougera. */
  lastBucketPartial?: boolean;
  /** Seaux sans observation a l'interieur de la serie. Zero en regime normal. */
  gaps?: number;
  /**
   * Le prix courant, **commun aux quatre periodes**.
   *
   * ⚠️ `last` est la cloture de la DERNIERE BOUGIE de la periode : un jour sur `all`, quatre heures
   * sur `1m`. S'en servir comme prix courant ferait afficher quatre « prix actuels » differents
   * selon l'onglet — le defaut « deux ecrans du meme wallet qui se contredisent ». `spot` vient
   * toujours du cache 24h, ou une bougie fait dix minutes.
   * ⚠️ Indicatif : le ticket d'ordre reste l'autorite (le devis borne son prix dans le carnet).
   */
  spot?: number | null;
  /** The rate of each pair, by the asset traded against CC (07/10): `CBTC` = `spot`, `CBNB` = the
   *  CC/BNB cross. Absent on an older server: the cBTC pair then falls back to `spot`. */
  spots?: Record<string, number | null>;
  spotAgeMs?: number | null;
  /** Le prix du CC en DOLLARS, courant. ⚠️ Meme regle que `spot` : commun aux quatre periodes,
   *  toujours tire du cache 24h — sinon quatre onglets affichent quatre prix du CC differents. */
  usdSpot?: number | null;
  /** Le CC en dollars sur la fenetre de CETTE periode, pour la variation et les bornes. Restreint
   *  aux memes horodatages que le croisement, donc les deux nombres du bandeau s'accordent. */
  usd?: { first: number; last: number; min: number; max: number; samples: number } | null;
  /** Each asset's dollar price, by instrument (`CC`, `CBTC`, `CBNB`), from the 24h cache (07/10). */
  usdSpots?: Record<string, number | null>;
  /** This period's dollar stats per instrument, on the CC's window; `usdBy.CC` is `usd`. */
  usdBy?: Record<string, { first: number; last: number; min: number; max: number; samples: number } | null>;
  from?: number;
  to?: number;
}

export function fetchCrossSeries(period: CrossPeriod): Promise<CrossSeries> {
  return call<CrossSeries>(`${BRIDGE_API}/trade/cross?period=${period}`);
}

export function fetchLeaderboard(scope: 'daily' | 'weekly' | 'lifetime') {
  return call<Leaderboard>(`${BRIDGE_API}/rewards/leaderboard?scope=${scope}`);
}
